import { VestigasHTTPError } from '../VestigasHTTPError';

import {
  CONNECTION_CHECK_MAX_POLL_FAILURES,
  CONNECTION_CHECK_POLL_INTERVAL_MS,
  CONNECTION_CHECK_TIMEOUT_MS,
} from './constants';
import { ConnectionCheckResponseError } from './parseConnectionCheck';
import { type ConnectionCheck } from './types';

/** The check did not finish in time, which says nothing about the connection. */
export class ConnectionCheckTimeoutError extends Error {
  constructor() {
    super('The connection check did not finish in time');
    this.name = 'ConnectionCheckTimeoutError';
  }
}

async function wait(delayMs: number, signal?: AbortSignal) {
  signal?.throwIfAborted();

  await new Promise<void>((resolve, reject) => {
    const timeout = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, delayMs);

    function onAbort() {
      clearTimeout(timeout);
      reject(signal?.reason);
    }

    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

/** Errors that asking again cannot fix. */
function isFinalPollError(error: unknown, signal?: AbortSignal) {
  return (
    signal?.aborted === true ||
    error instanceof ConnectionCheckResponseError ||
    (error instanceof VestigasHTTPError && error.response.status === 404)
  );
}

type RunConnectionCheckOptions = {
  /** Reads a started check by its id. */
  readonly fetchCheck: (
    checkId: string,
    signal?: AbortSignal,
  ) => Promise<ConnectionCheck>;
  /** Stops waiting, e.g. when the form closes. */
  readonly signal?: AbortSignal;
  /** Starts the check, which normally answers while it is still running. */
  readonly startCheck: (signal?: AbortSignal) => Promise<ConnectionCheck>;
};

/**
 * Starts a connection check and polls it until it has an outcome.
 *
 * @returns The check once it is no longer running; `state: failed` means the
 *   check itself broke and there is no result.
 * @throws ConnectionCheckTimeoutError when no outcome arrives in time, and the
 *   request error when starting fails or polling keeps failing.
 */
export async function runConnectionCheck({
  fetchCheck,
  signal,
  startCheck,
}: RunConnectionCheckOptions): Promise<ConnectionCheck> {
  // Counted from the start, so a slow start eats into the budget too.
  const deadline = Date.now() + CONNECTION_CHECK_TIMEOUT_MS;
  const started = await startCheck(signal);

  if (started.state !== 'running') {
    return started;
  }

  let consecutiveFailures = 0;

  while (Date.now() < deadline) {
    await wait(CONNECTION_CHECK_POLL_INTERVAL_MS, signal);

    try {
      const check = await fetchCheck(started.id, signal);
      consecutiveFailures = 0;

      if (check.state !== 'running') {
        return check;
      }
    } catch (error) {
      consecutiveFailures += 1;

      if (
        isFinalPollError(error, signal) ||
        consecutiveFailures >= CONNECTION_CHECK_MAX_POLL_FAILURES
      ) {
        throw error;
      }
    }
  }

  throw new ConnectionCheckTimeoutError();
}
